import {ReactNode, MouseEvent, KeyboardEvent, useState} from 'react';
import {Box, Typography, Grid} from '@mui/material';
import {t} from 'i18next';
import ArrRadio from '../../../../../common/arr-radio/ArrRadio';
import './IssueCodingTypeHeader.scss';
import ReplaceManualContentModal from './replace-manual-content-modal/ReplaceManualContentModal';
import SwitchToManualModal from './switch-to-manual-modal/SwitchToManualModal';
import {RootState} from '../../../../../../redux/store';
import {useDispatch, useSelector} from 'react-redux';
import {setDefineCriteria} from '../../../../../../redux/slice/aiReviewSlice';

/** Constants for criteria values (no magic strings) */
export const CRITERIA = {
  MANUAL: 'manual',
  SAVED: 'saved',
} as const;

type CriteriaValue = (typeof CRITERIA)[keyof typeof CRITERIA];

type CriteriaOption = {
  value: CriteriaValue;
  label: string;
};

type CriteriaRadioConfig = {
  id: CriteriaValue;
  description: string | ReactNode;
  options: CriteriaOption[]; // usually single-option-per-card in your design
};

interface IssueCodingTypeHeaderProps {
  isProtocolDisabled: boolean;
  handleUseSavedProtocol: () => void;
  clearAllContents: () => void;
}

const IssueCodingTypeHeader = ({
  isProtocolDisabled,
  handleUseSavedProtocol,
  clearAllContents,
}: IssueCodingTypeHeaderProps) => {
  const [showReplaceManualModal, setShowReplaceManualModal] =
    useState<boolean>(false);
  const [showSwitchToManualModal, setShowSwitchToManualModal] =
    useState<boolean>(false);
  const defineCriteria = useSelector(
    (state: RootState) => state.aiReview.defineCriteria
  );
  const dispatch = useDispatch();

  const criteriaRadioOptions: CriteriaRadioConfig[] = [
    {
      id: CRITERIA.MANUAL,
      description: t(
        'ai.review.newJobDetail.defineCriteriaDescriptionManually'
      ),
      options: [
        {
          value: CRITERIA.MANUAL,
          label: t('ai.review.newJobDetail.defineCriteriaManuallyLabel'),
        },
      ],
    },
    {
      id: CRITERIA.SAVED,
      description: t('ai.review.newJobDetail.defineCriteriaDescriptionSaved'),
      options: [
        {
          value: CRITERIA.SAVED,
          label: t('ai.review.newJobDetail.defineCriteriaSavedLabel'),
        },
      ],
    },
  ];

  const cards = criteriaRadioOptions;

  const handleReplaceManualModalClose = () => {
    setShowReplaceManualModal(false);
  };
  const handleSwitchToManualClose = () => {
    setShowSwitchToManualModal(false);
  };

  const selectValue = (next: string) => {
    if (defineCriteria !== next) dispatch(setDefineCriteria(next));
  };

  const handleRadioChange = (val: string) => {
    if (val === CRITERIA.SAVED && defineCriteria === CRITERIA.MANUAL) {
      setShowReplaceManualModal(true);
      return;
    }
    if (val === CRITERIA.MANUAL && defineCriteria === CRITERIA.SAVED) {
      setShowSwitchToManualModal(true);
      return;
    }
    dispatch(setDefineCriteria(val));
  };

  // Clicking anywhere on the card selects the radio
  const handleCardClick =
    (value: CriteriaValue) => (_e: MouseEvent<HTMLDivElement>) => {
      if (value === CRITERIA.SAVED && defineCriteria === CRITERIA.MANUAL) {
        setShowReplaceManualModal(true);
      }
      if (value === CRITERIA.MANUAL && defineCriteria === CRITERIA.SAVED) {
        setShowSwitchToManualModal(true);
      }
      selectValue(value);
    };

  // Keyboard: Space/Enter selects the card (WAI-ARIA radio pattern)
  const handleCardKeyDown =
    (value: CriteriaValue) => (e: KeyboardEvent<HTMLDivElement>) => {
      const key = e.key.toLowerCase();
      if (key === ' ' || key === 'enter') {
        e.preventDefault();
        if (value === CRITERIA.SAVED && defineCriteria === CRITERIA.MANUAL) {
          setShowReplaceManualModal(true);
        }
        if (value === CRITERIA.MANUAL && defineCriteria === CRITERIA.SAVED) {
          setShowSwitchToManualModal(true);
        }
        selectValue(value);
      }
    };

  return (
    <div
      className="issue-coding-type-header"
      data-testid={`issue-coding-type-header-issue-coding-header-title-wrapper`}
    >
      <Box>
        <Typography
          className="text-headings typo-text subhead4_size"
          data-testid="issue-coding-header-title"
          tabIndex={0}
        >
          {t('ai.review.newJobDetail.issueCodingHeaderTitle')}
        </Typography>
      </Box>
      <Box className="define-criteria">
        <Grid
          container
          spacing={3}
          role="radiogroup"
          aria-label={t('ai.review.newJobDetail.defineCriteriaAriaLabel')}
          data-testid="define-criteria-radiogroup"
        >
          {cards.map(({id, description, options}) => {
            const selected = defineCriteria === id;
            return (
              <Grid
                key={id}
                size={{xs: 12, sm: 4}}
                className="define-criteria-box"
                data-testid={`criteria-grid-item-${id}`}
              >
                <Box
                  role="radio"
                  aria-checked={selected}
                  tabIndex={!isProtocolDisabled || selected ? 0 : -1}
                  onClick={
                    !isProtocolDisabled ? handleCardClick(id) : undefined
                  }
                  className={`criteria-card ${selected ? 'is-selected' : ''}`}
                  onKeyDown={handleCardKeyDown(id)}
                  data-testid={`criteria-card-${id}`}
                >
                  <span
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`issue-coding-type-header-span-${id}`}
                  >
                    <ArrRadio
                      name="define-criteria"
                      className="define-criteria-radio"
                      value={defineCriteria}
                      isAriaDisabled={
                        isProtocolDisabled && id === CRITERIA.SAVED
                      }
                      disabled={isProtocolDisabled && id === CRITERIA.SAVED}
                      onChange={(val: string) => handleRadioChange(val)}
                      data={options}
                      size="small"
                      dataTestId={`criteria-card-${id}-radio`}
                      tabIndex={-1}
                      ariaLabel={
                        id === CRITERIA.MANUAL
                          ? t(
                              'ai.review.newJobDetail.defineCriteriaManuallyLabel'
                            )
                          : t('ai.review.newJobDetail.defineCriteriaSavedLabel')
                      }
                    />
                  </span>

                  <Typography
                    className="typo-text body4_size box-subtitle"
                    data-testid={`criteria-card-${id}-description`}
                  >
                    {description}
                  </Typography>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Box>
      <ReplaceManualContentModal
        open={showReplaceManualModal}
        onClose={handleReplaceManualModalClose}
        handleUseSavedProtocol={handleUseSavedProtocol}
        setShowReplaceManualModal={setShowReplaceManualModal}
      />
      <SwitchToManualModal
        open={showSwitchToManualModal}
        onClose={handleSwitchToManualClose}
        setShowSwitchToManualModal={setShowSwitchToManualModal}
        clearAllContents={clearAllContents}
      />
    </div>
  );
};

export default IssueCodingTypeHeader;
